import { MPCRowContainerModule } from "./Rows/rowContainer";
import { MPCControlsRow } from "./Rows/controls-row";
import { MPCColumnHeader } from "./Columns/column-header";
import { MPCDeviceDetectionHelper } from "../helpers/deviceDetectionHelper";
import { MPGridColumnLock } from "./gridColumnLock";
import { MPCGridCarousel } from "./gridCarousel";
import { MPCProductsCounter } from "./productsCounter";
import { MPCPriceValuesRowModule } from "./Rows/priceValuesRow";
import { MPCScrollLock } from "../helpers/scrollLock";
const PlusIcon2021 = require("../../images/plus-2021.svg") as string;
const BasketIcon2021 = require("../../images/basket-2021.svg") as string;
const RecycleBinIcon = require("../../images/icon_remove.svg") as string;
const LockedIcon = require("../../images/locked.svg") as string;

export module MPCGridController {
    import RowContainer = MPCRowContainerModule.RowContainer;
    import ControlsRow = MPCControlsRow.ControlsRow;
    import ColumnHeader = MPCColumnHeader.ColumnHeader;
    import DeviceDetectionHelper = MPCDeviceDetectionHelper.DeviceDetectionHelper;
    import GridColumnLockHelper = MPGridColumnLock.GridColumnLock;
    import GridCarousel = MPCGridCarousel.GridCarousel;
    import ProductsCounter = MPCProductsCounter.ProductsCounter;
    import PriceValuesRow = MPCPriceValuesRowModule.PriceValuesRow;
    import ScrollLock = MPCScrollLock.ScrollLock;

    export class GridController {
        private scope: HTMLElement;
        private rowContainers: Array<RowContainer> = new Array<RowContainer>();
        private grid: HTMLElement;
        private columnHeadersArray: Array<HTMLElement>;
        private gridCarousel : GridCarousel;
        private deviceDetectionHelper : DeviceDetectionHelper;
        private filledColumns: number;
        private categoryName: string;
        private orderModalExists: boolean;
        private scrollLock: ScrollLock;

        constructor(scope: HTMLElement) {
            this.scope = scope;
            this.init();
        }

        public gridHtmlElement(): HTMLElement {
            return this.grid;
        }

        private init(): void {
            this.deviceDetectionHelper = DeviceDetectionHelper.instance;
            this.scrollLock = ScrollLock.instance;
            this.grid = this.scope.querySelector('.grid');

            // Collect grid column headers:
            const columnHeaders: NodeListOf<HTMLElement> = this.grid.querySelectorAll('.column-header-container');
            this.columnHeadersArray = Array.prototype.slice.call(columnHeaders);

            if (!this.grid) {
                return;
            }

            this.initOrderModalMutationObserver();
            this.filledColumns = this.grid.querySelectorAll('.header:not(.empty-header)').length;

            if(this.deviceDetectionHelper.isMobileShop()) {
                const tmpCarouselElement: HTMLElement = this.scope.querySelector('.navigation-container');
                this.gridCarousel = new GridCarousel(this.scope, tmpCarouselElement);
                
                new ProductsCounter(tmpCarouselElement).updateProductsCounter(this.filledColumns);
                new GridColumnLockHelper(this.scope, this.gridCarousel);
            }

            this.categoryName = this.grid.getAttribute('data-pcp-navigation-key');

            this.registerEvents();
            this.setFirstAndLastClass();
            scrollTo();
            this.loadGridIcons();

            // update prices row. This is only for prices row:
            new PriceValuesRow();
        }

        // Dedicated for quick order modal.
        // This element is added to dom dynamically so we that tracking when it is done and locking background.
        // This feature, more/ or less, dedicated for tablet devices, but is suitable for desktop too.
        private initOrderModalMutationObserver(): void {
            const observer: MutationObserver = new MutationObserver(() => {
                const orderModal = document.body.querySelector('.modal-scrollable');
                let quickOrderModalExists: boolean = document.body.contains(orderModal);

                if (quickOrderModalExists && !this.orderModalExists) {
                    this.scrollLock.lock();
                    this.orderModalExists = true;
                }
                else if (this.orderModalExists && !quickOrderModalExists) {
                    this.scrollLock.unlock();
                    this.orderModalExists = false;
                }
            });

            observer.observe(document.body, { childList: true });
        }

        private loadGridIcons(): void {
            const filledColumnsHeaders: NodeListOf<HTMLElement> = this.grid.querySelectorAll('.header:not(.empty-header)');
            const filledColumnsHeadersArray: Array<HTMLElement> = Array.prototype.slice.call(filledColumnsHeaders);
            const emptyColumnsHeaders: NodeListOf<HTMLElement> = this.grid.querySelectorAll('.empty-header');
            const emptyColumnsHeadersArray: Array<HTMLElement> = Array.prototype.slice.call(emptyColumnsHeaders);
            const emptySlides: NodeListOf<HTMLElement> = this.scope.querySelectorAll('.empty-slide');
            const emptySlidesArray: Array<HTMLElement> = Array.prototype.slice.call(emptySlides);

            const lockedColumnIcons: NodeListOf<HTMLElement> = this.scope.querySelectorAll('.mpc-locked-column');
            const lockedColumnIconsArray: Array<HTMLElement> = Array.prototype.slice.call(lockedColumnIcons);

            for (const tmpColumnHeader of filledColumnsHeadersArray) {
                tmpColumnHeader.querySelector('.mpc-basket').innerHTML = BasketIcon2021;
                tmpColumnHeader.querySelector('.mpc-recycle').innerHTML = RecycleBinIcon;
            }

            for (const tmpColumnHeader of emptyColumnsHeadersArray) {
                tmpColumnHeader.querySelector('.mpc-plus').innerHTML = PlusIcon2021;

                tmpColumnHeader.addEventListener('keydown', (event: any) => {
                    // prevent page jump down on Space btn., click
                    if (event.code === 'Space')
                        event.preventDefault(); 
                });
            }

            for (const emptySlide of emptySlidesArray) {
                emptySlide.querySelector('.mpc-plus').innerHTML = PlusIcon2021;
            }

            for (const lockColumnIcon of lockedColumnIconsArray) {
                lockColumnIcon.innerHTML = LockedIcon;
            }
        }

        private registerEvents(): void {
            let columnNumber: number = 1;
            let lastColumn: boolean;
            let firstColumn: boolean;
            for (const tmpColumnHeader of this.columnHeadersArray) {
                let columnHeader: ColumnHeader = new ColumnHeader();

                if(columnNumber == 1)
                    firstColumn = true;

                if(columnNumber == 4)
                    lastColumn = true;

                columnHeader.init(tmpColumnHeader, this.grid, lastColumn, firstColumn);
                columnNumber++;
                firstColumn = false;
                lastColumn = false;
            }

            // register simple row events:
            const rowContainers: NodeListOf<HTMLElement> = this.grid.querySelectorAll('.row-container');
            const rowContainersArray: Array<HTMLElement> = Array.prototype.slice.call(rowContainers);

            const mpcModalContainer: HTMLElement = this.scope.querySelector('.mpc-modal-container');

            for (const rowContainer of rowContainersArray) {
                const gridRow: RowContainer = new RowContainer();
                gridRow.init(rowContainer, this.filledColumns, mpcModalContainer, this.categoryName);
                this.rowContainers.push(gridRow);
            }

            // registers control rows events:
            const controlsRowHtml: HTMLElement = this.grid.querySelector('.controls');

            if (controlsRowHtml) {
                const controlsRow: ControlsRow = new ControlsRow();
                controlsRow.init(controlsRowHtml, this.rowContainers,
                    this.columnHeadersArray.filter(it => it.querySelector('.header').classList.contains('empty-header')).length, this, this.columnHeadersArray[0]);
            }
        }

        public setFirstAndLastClass(): void {
            for (const mainHeadlineRow of this.rowContainers) {
                mainHeadlineRow.setFirstAndLastClass();
            }
        }
    }
}